import React, { useState, useEffect } from "react"
import {
  Input,
  Label,
  FormGroup,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap"
import ContactEditModal from "./ContactEditModal"
import axios from "axios"
import Select from "react-select"
import apiURl from "url"
import toastr from "toastr"
import "toastr/build/toastr.min.css"

const ContactSelector = ({
  type,
  onChange,
  selectTheme,
  resetSignal,
  selectStyles,
  selectStylesforSearch,
  loadCities = () => {},
  initialData = null,
  enableSearch = true,
  citiesData = [],
  receiverCountryOptions = null,
}) => {
  const [searchOptions, setSearchOptions] = useState([])
  const [selectedOption, setSelectedOption] = useState(null)
  const [editModalOpen, setEditModalOpen] = useState(false)

  // Add new city modal states
  const [addCityModalOpen, setAddCityModalOpen] = useState(false)
  const [newCityName, setNewCityName] = useState("")
  const [pendingCitySelect, setPendingCitySelect] = useState(null)

  const [contact, setContact] = useState(
    initialData || {
      first_name: "",
      last_name: "",
      phone: "",
      phone2: "",
      email: "",
      cnic: "",
      city_name: "",
      zip: "",
      current_address: "",
      permanent_address: "",
      full_address: "",
      country_name: "",
    }
  )

  const isDisabled = selectedOption !== null
  const isCityDisabled = type === "receiver" && !contact.country_name

  const updateParent = updated => onChange(updated)

  // =========================================================
  // SEARCH CONTACTS
  // =========================================================
  const searchContacts = async q => {
    if (!q || q.length < 2) return

    const res = await axios.get(`${apiURl}/international/contacts/search`, {
      params: { q, type },
    })

    const options = (res.data.contacts || []).map(c => ({
      value: c.phone,
      label: `${c.first_name} ${c.last_name} (${c.phone})`,
      data: c,
    }))

    setSearchOptions(options)
  }

  const selectContact = option => {
    if (!option) {
      // reset contact
      const empty = {
        first_name: "",
        last_name: "",
        phone: "",
        phone2: "",
        email: "",
        cnic: "",
        city_name: "",
        zip: "",
        current_address: "",
        permanent_address: "",
        full_address: "",
        country_name: "",
      }
      setSelectedOption(null)
      setContact(empty)
      updateParent(empty)
      return
    }

    setSelectedOption(option)
    setSearchOptions([])
    setContact(option.data)
    updateParent(option.data)
  }

  // =========================================================
  // HANDLE INPUT CHANGE
  // =========================================================
  const handleField = (field, val) => {
    const updated = { ...contact, [field]: val }
    setContact(updated)
    if (!isDisabled) updateParent(updated)
  }

  // Reset state when parent resets
  useEffect(() => {
    setSelectedOption(null)
    setSearchOptions([])

    const empty = {
      first_name: "",
      last_name: "",
      phone: "",
      phone2: "",
      email: "",
      cnic: "",
      city_name: "",
      zip: "",
      current_address: "",
      permanent_address: "",
      full_address: "",
      country_name: "",
    }

    setContact(empty)
    onChange(empty)
  }, [resetSignal])

  // =========================================================
  // ADD NEW CITY
  // =========================================================
  const handleAddCity = async () => {
    if (!newCityName.trim()) return

    let finalCountry =
      type === "sender" ? "Pakistan" : contact.country_name || null

    if (!finalCountry) {
      toastr.error("Please select a country first.")
      return
    }

    try {
      await axios.post(`${apiURl}/international/cities-options/add`, {
        city_name: newCityName.trim(),
        country_name: finalCountry,
      })

      // auto update UI
      setPendingCitySelect(newCityName.trim())
      handleField("country_name", finalCountry)

      setAddCityModalOpen(false)
      setNewCityName("")

      loadCities()

      toastr.success("City added successfully!")
    } catch (err) {
      console.error(err)
      toastr.error("Failed to add city")
    }
  }

  useEffect(() => {
    if (pendingCitySelect && citiesData.length > 0) {
      // Find if the newly added city exists now
      const exists = citiesData.find(c => c.name === pendingCitySelect)

      if (exists) {
        handleField("city_name", pendingCitySelect)
        setPendingCitySelect(null)
      }
    }
  }, [citiesData])

  useEffect(() => {
    if (type === "receiver" && !isDisabled) {
      handleField("city_name", "")
    }
  }, [contact.country_name])

  return (
    <div className="contact-box">
      {/* =========================================
          SEARCH SELECT BOX
      ========================================== */}
      {enableSearch && (
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Label className="fw-semibold mb-0">
              Search {type === "receiver" ? "Consignee" : "Shipper"}
            </Label>

            {selectedOption && (
              <i
                className="bx bx-edit text-primary"
                style={{ cursor: "pointer", fontSize: "18px" }}
                onClick={() => setEditModalOpen(true)}
              ></i>
            )}
          </div>

          <Select
            options={searchOptions}
            onInputChange={value => {
              searchContacts(value)
              return value
            }}
            onChange={selectContact}
            placeholder="Search by phone, name.."
            value={selectedOption}
            onMenuClose={() => setSearchOptions([])}
            theme={selectTheme}
            styles={selectStylesforSearch}
            isClearable
          />
        </div>
      )}

      {/* FIRST NAME */}
      <FormGroup>
        <Label className="fw-semibold text-dark">
          First Name <span className="text-danger">*</span>
        </Label>
        <Input
          disabled={isDisabled}
          placeholder="Enter first name"
          value={contact.first_name}
          onChange={e => handleField("first_name", e.target.value)}
          className="form-control sleek-input"
          required
        />
      </FormGroup>

      {/* LAST NAME */}
      <FormGroup>
        <Label className="fw-semibold text-dark">
          Last Name <span className="text-danger">*</span>
        </Label>
        <Input
          disabled={isDisabled}
          placeholder="Enter last name"
          value={contact.last_name}
          onChange={e => handleField("last_name", e.target.value)}
          className="form-control sleek-input"
          required
        />
      </FormGroup>

      {/* PHONE */}
      <FormGroup>
        <Label className="fw-semibold text-dark">
          Phone Number <span className="text-danger">*</span>
        </Label>
        <Input
          disabled={isDisabled}
          placeholder="03XXXXXXXXX"
          value={contact.phone}
          onChange={e => handleField("phone", e.target.value)}
          className="form-control sleek-input"
          required
        />
      </FormGroup>

      {/* EMAIL */}
      <FormGroup>
        <Label className="fw-semibold text-dark">Email</Label>
        <Input
          disabled={isDisabled}
          placeholder="Enter email"
          value={contact.email}
          onChange={e => handleField("email", e.target.value)}
          className="form-control sleek-input"
        />
      </FormGroup>

      {/* CNIC (Sender Only) */}
      {type === "sender" && (
        <FormGroup>
          <Label className="fw-semibold text-dark">
            CNIC <span className="text-danger">*</span>
          </Label>
          <Input
            disabled={isDisabled}
            placeholder="XXXXX-XXXXXXX-X"
            value={contact.cnic}
            onChange={e => handleField("cnic", e.target.value)}
            className="form-control sleek-input"
            required
          />
        </FormGroup>
      )}

      {/* COUNTRY (Receiver Only) */}
      {type === "receiver" && (
        <FormGroup>
          <Label className="fw-semibold text-dark">
            Country <span className="text-danger">*</span>
          </Label>
          <Select
            options={receiverCountryOptions}
            value={
              receiverCountryOptions?.find(
                opt => opt.value === contact.country_name
              ) || null
            }
            onChange={opt => handleField("country_name", opt.value)}
            placeholder="Select country"
            theme={selectTheme}
            styles={selectStyles}
            isDisabled={isDisabled}
            required
          />
        </FormGroup>
      )}

      {/* CITY SELECT */}
      <FormGroup>
        <Label className="fw-semibold text-dark">
          City <span className="text-danger">*</span>
        </Label>

        <Select
          options={citiesData.map(c => ({
            value: c.name,
            label: c.name,
          }))}
          value={
            contact.city_name
              ? { value: contact.city_name, label: contact.city_name }
              : null
          }
          onChange={opt => handleField("city_name", opt?.value || "")}
          placeholder={isCityDisabled ? "Select country first" : "Select city"}
          isDisabled={isDisabled || isCityDisabled}
          theme={selectTheme}
          styles={selectStyles}
          required
        />

        {/* ADD NEW CITY BUTTON */}
        <Button
          color="link"
          className="p-0 mt-1"
          disabled={isDisabled || isCityDisabled}
          onClick={() => setAddCityModalOpen(true)}
        >
          + Add New City
        </Button>
      </FormGroup>

      {/* ZIP CODE */}
      <FormGroup>
        <Label className="fw-semibold text-dark">
          ZIP Code{" "}
          {type === "receiver" && <span className="text-danger">*</span>}
        </Label>
        <Input
          disabled={isDisabled}
          placeholder="Enter ZIP"
          value={contact.zip}
          onChange={e => handleField("zip", e.target.value)}
          className="form-control sleek-input"
          required={type === "receiver"}
        />
      </FormGroup>

      {/* ADDRESS */}
      <FormGroup>
        <Label className="fw-semibold text-dark">
          Address <span className="text-danger">*</span>
        </Label>
        <Input
          type="textarea"
          rows="2"
          placeholder="Enter full address"
          disabled={isDisabled}
          value={contact.current_address || contact.full_address}
          onChange={e =>
            handleField(
              type === "sender" ? "current_address" : "full_address",
              e.target.value
            )
          }
          className="form-control sleek-input"
          required
        />
      </FormGroup>

      {/* ========================================================
          ADD CITY MODAL
      ========================================================= */}
      <Modal
        isOpen={addCityModalOpen}
        toggle={() => setAddCityModalOpen(false)}
      >
        <ModalHeader toggle={() => setAddCityModalOpen(false)}>
          Add New City
        </ModalHeader>

        <ModalBody>
          <Label className="fw-semibold">City Name</Label>
          <Input
            placeholder="Enter city name"
            value={newCityName}
            onChange={e => setNewCityName(e.target.value)}
            className="form-control sleek-input mb-3"
          />

          <Label className="fw-semibold">Country</Label>
          <Input
            disabled
            value={
              type === "sender"
                ? "Pakistan"
                : contact.country_name || "No country selected"
            }
            className="form-control sleek-input mb-3"
          />

          <Button
            color="primary"
            className="w-100"
            onClick={handleAddCity}
            disabled={!newCityName.trim()}
          >
            Add City
          </Button>
        </ModalBody>
      </Modal>

      {/* ========================================================
          EDIT CONTACT MODAL
      ========================================================= */}
      {editModalOpen && (
        <ContactEditModal
          isOpen={editModalOpen}
          toggle={() => setEditModalOpen(false)}
          contact={contact}
          type={type}
          citiesData={citiesData}
          receiverCountryOptions={receiverCountryOptions}
          loadCities={loadCities}
          onSave={updated => {
            setContact(updated)
            updateParent(updated)
            setSelectedOption({
              value: updated.phone,
              label: `${updated.first_name} ${updated.last_name} (${updated.phone})`,
              data: updated,
            })
          }}
        />
      )}
    </div>
  )
}

export default ContactSelector
